// Pure logic behind the Lead Routes edit form (docs/lead-relay.md §10.1).
//
// ⚠️ A ROUTE DECIDES WHICH DEALERSHIP — OR CLUB — RECEIVES A CLIENT'S LEADS. So
// the form marks every field that differs from what is saved (the grids'
// modification dot, applied to a form), and a save first states what is about
// to change in words — above all a change to the one field that picks the
// destination (`lid` / `clubId`), which silently re-points every future lead.
//
// ⚠️⚠️ PROVIDER-AWARE (step 3, 2026-10-06). The state is a DISCRIMINATED UNION
// on `provider`, like LeadRoute.config: a VirtualYard form carries VirtualYard's
// fields and an Anytime Fitness form carries Anytime Fitness's, and neither
// carries the other's. There is no empty `formUrl` on an AF form to satisfy a
// type, and no greyed-out VirtualYard field to render.

import type { LeadProvider, LeadRouteRecord } from '../../types/leadRelay.js';
import { AF_COUNTRY_VALUES } from './anytimefitness.js';
import { type LeadLocale } from './locale.js';
import { BYD_MODEL_MAP_STANDARD_ID, type ModelMapOverrides } from './modelMapStandard.js';
import { normalizeKey } from './normalize.js';
import { providerVocabulary } from './providerVocabulary.js';
import type { LeadRouteFields } from './routeRules.js';
import { DEFAULT_MESSAGE_TEMPLATE, SENDABLE_INTEREST_OPTIONS, VIRTUALYARD_SUBMIT_URL } from './virtualyard.js';

/** The provider-neutral part of the form. */
export interface RouteFormCommon {
  id: string;
  name: string;
  accountId: string;
  active: boolean;
  testMode: boolean;
  metaFormNames: string[];
  alertChannel: string;
}

export interface VirtualYardFormFields {
  /** ⛔ Set at create, displayed thereafter, NEVER editable — see LeadRouteFields.provider. */
  readonly provider: 'virtualyard';
  formUrl: string;
  lid: string;
  defaultInterest: string;
  allowedInterests: string[];
  /** The shared standard it inherits from — set by the setup, not edited here. */
  readonly modelMapStandardId: string | null;
  /** ⭐ Only this route's departures from the standard (§25). Edited row by row. */
  modelMapOverrides: ModelMapOverrides;
  messageTemplate: string;
}

export interface AnytimeFitnessFormFields {
  /** ⛔ Set at create, displayed thereafter, NEVER editable. */
  readonly provider: 'anytimefitness';
  /** ⚠️ The ONE club-identifying field — it writes both location fields of the payload. */
  clubId: string;
  /** Fixed by the setup; the per-country values are READ from it, never stored beside it. */
  readonly country: LeadLocale;
}

export type RouteFormState = RouteFormCommon & (VirtualYardFormFields | AnytimeFitnessFormFields);

/** Every field a person can change, across providers. ⛔ Never the provider (or the AF country). */
export type CommonFormField = keyof Omit<RouteFormCommon, 'id'>;
export type VirtualYardFormField = keyof Omit<VirtualYardFormFields, 'provider' | 'modelMapStandardId'>;
export type AnytimeFitnessFormField = keyof Omit<AnytimeFitnessFormFields, 'provider' | 'country'>;
export type RouteFormField = CommonFormField | VirtualYardFormField | AnytimeFitnessFormField;

const COMMON_FIELDS: readonly CommonFormField[] = ['name', 'accountId', 'active', 'testMode', 'metaFormNames', 'alertChannel'];
const PROVIDER_FIELDS: { [P in LeadProvider]: readonly RouteFormField[] } = {
  virtualyard: ['lid', 'formUrl', 'defaultInterest', 'allowedInterests', 'modelMapOverrides', 'messageTemplate'],
  anytimefitness: ['clubId'],
};

/** The fields a form of this provider has, in display order. */
export function fieldsForProvider(provider: LeadProvider): RouteFormField[] {
  return [...COMMON_FIELDS, ...PROVIDER_FIELDS[provider]];
}

function emptyCommon(): RouteFormCommon {
  return {
    id: '',
    name: '',
    accountId: '',
    // ⚠️ A new route starts unable to receive anything, like a provisioned one:
    // inactive AND in test mode — the double lock (form names no longer route,
    // so an empty list protects nothing; docs/lead-relay.md §25).
    active: false,
    testMode: true,
    metaFormNames: [],
    alertChannel: '',
  };
}

export function emptyVirtualYardForm(): RouteFormState {
  return {
    ...emptyCommon(),
    provider: 'virtualyard',
    formUrl: '',
    lid: '',
    defaultInterest: 'General Enquiry',
    allowedInterests: [...SENDABLE_INTEREST_OPTIONS],
    // ⭐ A new BYD route inherits the shared BYD standard with NO overrides, so
    // every later change to the standard reaches it (§25).
    modelMapStandardId: BYD_MODEL_MAP_STANDARD_ID,
    modelMapOverrides: {},
    messageTemplate: DEFAULT_MESSAGE_TEMPLATE,
  };
}

export function emptyAnytimeFitnessForm(country: LeadLocale): RouteFormState {
  return { ...emptyCommon(), provider: 'anytimefitness', clubId: '', country };
}

function assertNever(value: never): never {
  throw new Error(`Unhandled lead provider: ${JSON.stringify(value)}`);
}

export function formFromRoute(route: LeadRouteRecord): RouteFormState {
  const common: RouteFormCommon = {
    id: route.id,
    name: route.name,
    accountId: route.accountId,
    active: route.active,
    testMode: route.testMode,
    metaFormNames: [...route.metaFormNames],
    alertChannel: route.alertChannel ?? '',
  };
  switch (route.provider) {
    case 'virtualyard':
      return {
        ...common,
        provider: 'virtualyard',
        formUrl: route.formUrl,
        lid: route.fixed.lid,
        defaultInterest: route.defaultInterest,
        allowedInterests: [...route.allowedInterests],
        modelMapStandardId: route.modelMapStandardId,
        modelMapOverrides: { ...route.modelMapOverrides },
        messageTemplate: route.messageTemplate,
      };
    case 'anytimefitness':
      return { ...common, provider: 'anytimefitness', clubId: route.clubId, country: route.country };
    default:
      return assertNever(route);
  }
}

export function fieldsFromForm(form: RouteFormState): LeadRouteFields {
  const common = {
    name: form.name,
    accountId: form.accountId,
    active: form.active,
    testMode: form.testMode,
    metaFormNames: form.metaFormNames.map((n) => n.trim()).filter(Boolean),
    alertChannel: form.alertChannel.trim() === '' ? null : form.alertChannel.trim(),
  };
  switch (form.provider) {
    case 'virtualyard': {
      const modelMapOverrides: ModelMapOverrides = {};
      for (const [answer, value] of Object.entries(form.modelMapOverrides)) {
        const key = normalizeKey(answer);
        if (key !== '' && value !== '') modelMapOverrides[key] = value;
      }
      return {
        ...common,
        provider: 'virtualyard',
        formUrl: form.formUrl,
        submitUrlBase: VIRTUALYARD_SUBMIT_URL,
        lid: form.lid,
        defaultInterest: form.defaultInterest,
        allowedInterests: form.allowedInterests,
        modelMapStandardId: form.modelMapStandardId,
        modelMapOverrides,
        messageTemplate: form.messageTemplate,
      };
    }
    case 'anytimefitness':
      return { ...common, provider: 'anytimefitness', clubId: form.clubId, country: form.country };
    default:
      return assertNever(form);
  }
}

export const ROUTE_FIELD_LABELS: Record<RouteFormField, string> = {
  name: 'Name',
  accountId: 'Account',
  active: 'Active',
  testMode: 'Test mode',
  formUrl: 'Form URL',
  lid: 'Store id (lid)',
  metaFormNames: 'Meta form names',
  defaultInterest: 'Default interest',
  allowedInterests: 'Allowed interests',
  modelMapOverrides: 'Model map',
  messageTemplate: 'Message template',
  alertChannel: 'Alert channel',
  clubId: 'Club id',
};

/** The value of a field in a comparable form; '' for a field this provider does not have. */
function canonical(form: RouteFormState, field: RouteFormField): string {
  const fields = fieldsFromForm(form) as unknown as Record<string, unknown>;
  switch (field) {
    case 'modelMapOverrides':
      return JSON.stringify(
        Object.entries((fields.modelMapOverrides ?? {}) as ModelMapOverrides).sort(([a], [b]) => a.localeCompare(b)),
      );
    case 'allowedInterests':
      return JSON.stringify([...((fields.allowedInterests ?? []) as string[])].sort());
    case 'lid':
    case 'clubId':
      return String(fields[field] ?? '').trim();
    case 'alertChannel':
      return String(fields.alertChannel ?? '');
    default:
      return JSON.stringify(fields[field] ?? null);
  }
}

/** Fields that differ from what is saved. A new route has no saved state: []. */
export function changedFields(saved: RouteFormState | null, current: RouteFormState): RouteFormField[] {
  if (!saved) return [];
  return fieldsForProvider(current.provider).filter((field) => canonical(saved, field) !== canonical(current, field));
}

/** The one field that picks the destination, as `[field, value]`. */
function destinationOf(form: RouteFormState): { field: 'lid' | 'clubId'; value: string } {
  return form.provider === 'virtualyard' ? { field: 'lid', value: form.lid.trim() } : { field: 'clubId', value: form.clubId.trim() };
}

/**
 * What the confirmation says before a save. The consequential changes are
 * spelled out; the rest are listed by name.
 */
export function saveConsequences(
  saved: RouteFormState | null,
  current: RouteFormState,
  inFlightCount: number,
): string[] {
  if (!saved) {
    return [
      current.active
        ? "This route will be ACTIVE as soon as it is saved: leads carrying its account's account_id will start arriving."
        : 'This route will be saved inactive — it receives nothing until it is activated.',
    ];
  }
  const vocab = providerVocabulary(current.provider);
  const changed = changedFields(saved, current);
  const lines: string[] = [];
  const dest = destinationOf(current);
  if (changed.includes(dest.field)) {
    lines.push(
      `The ${vocab.destinationId.toLowerCase()} changes from ${destinationOf(saved).value} to ${dest.value}. Every lead from now on goes to THAT ${vocab.destination.toLowerCase()} — check it is the right one before saving.`,
    );
  }
  if (changed.includes('formUrl')) {
    lines.push('The form URL changes. If this is a token rotation, every other route on the same form needs it too.');
  }
  if (!saved.active && current.active) {
    lines.push("The route becomes ACTIVE: leads carrying its account's account_id will start arriving.");
  }
  if (saved.active && !current.active) {
    lines.push("The route becomes inactive: new leads for its account will land UNASSIGNED in the Lead Log and alert, until someone assigns them.");
  }
  if (!saved.testMode && current.testMode && inFlightCount > 0) {
    lines.push(
      `${inFlightCount} lead${inFlightCount === 1 ? ' is' : 's are'} already queued for this route and WILL STILL be submitted — test mode stops new leads only.`,
    );
  }
  if (saved.testMode && !current.testMode) {
    lines.push(`Test mode is switched OFF: leads will be submitted to ${vocab.system} for real.`);
  }
  const named = changed.filter((f) => ![dest.field, 'formUrl', 'active', 'testMode'].includes(f));
  if (named.length > 0) lines.push(`Also changing: ${named.map((f) => ROUTE_FIELD_LABELS[f]).join(', ')}.`);
  return lines;
}

/** §8 — inactive → active is the moment to send a test lead. */
export function shouldPromptTestLead(saved: RouteFormState | null, current: RouteFormState): boolean {
  return current.active && (saved === null || !saved.active);
}

/** The lead the message preview renders. */
export const SAMPLE_PREVIEW_LEAD = {
  campaign_name: 'Spring Shark Launch',
  form_name: 'BYD Liverpool — Spring',
  model: 'BYD SHARK 6',
  extra: { 'When are you looking to buy?': 'In the next month' },
};

/** Add or remove an item from a string list, keeping the original order. */
export function toggleListItem(list: readonly string[], item: string, on: boolean): string[] {
  if (on) return list.includes(item) ? [...list] : [...list, item];
  return list.filter((x) => x !== item);
}

/**
 * Form hosts whose ACTIVE routes carry more than one token tail — the list
 * page's version of the save-time sharedCredentialWarnings, over the list rows.
 */
export function sharedFormsWithMixedTokens(
  routes: ReadonlyArray<{ active: boolean; formHost: string | null; formUrlTokenTail: string | null }>,
): string[] {
  const tailsByHost = new Map<string, Set<string>>();
  for (const route of routes) {
    if (!route.active || !route.formHost) continue;
    const set = tailsByHost.get(route.formHost) ?? new Set<string>();
    set.add(route.formUrlTokenTail ?? 'none');
    tailsByHost.set(route.formHost, set);
  }
  return [...tailsByHost.entries()].filter(([, tails]) => tails.size > 1).map(([host]) => host).sort();
}

/** The per-country values an Anytime Fitness route sends — read from its country, never stored. */
export function anytimeFitnessCountryValues(country: LeadLocale) {
  return AF_COUNTRY_VALUES[country];
}
